import { storage } from "util/storage";

const STORAGE_PREFIX = "societea:";
const DRAFT_PREFIX = `${STORAGE_PREFIX}draft:`;

interface StoredDraftEnvelope {
  v?: {
    savedAtIso?: unknown;
    value?: unknown;
    version?: unknown;
  };
  e?: unknown;
}

export type DraftSource = "db" | "local";
export type DraftType = "post" | "comment";

export interface LocalDraftSummary {
  source: "local";
  draftType: DraftType;
  entityType: string;
  id: string;
  storageKey: string;
  logicalKey: string;
  kind: string;
  title: string;
  description: string;
  value: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  issue_id: number | null;
  commentable_type: string | null;
  commentable_id: number | null;
  parent_id: number | null;
  root_id: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseStoredDraft(raw: string | null): { savedAtIso: string | null; value: Record<string, unknown> } | null {
  if (!raw) {
    return null;
  }
  try {
    const envelope = JSON.parse(raw) as StoredDraftEnvelope;
    const expiresAt = typeof envelope.e === "number" ? envelope.e : null;
    if (!expiresAt || Date.now() > expiresAt) {
      return null;
    }
    const payload = isRecord(envelope.v) ? envelope.v : null;
    const savedAtIso = typeof payload?.savedAtIso === "string" ? payload.savedAtIso : null;
    const value = isRecord(payload?.value) ? payload.value : {};
    return { savedAtIso, value };
  } catch {
    return null;
  }
}

function parseNumber(value: string | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function readStringField(source: Record<string, unknown>, field: string): string {
  const value = source[field];
  return typeof value === "string" ? value : "";
}

function summarizeValue(value: Record<string, unknown>, fallback: string): string {
  const title = readStringField(value, "title").trim();
  if (title) {
    return title;
  }
  const content = readStringField(value, "content").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (content) {
    return content.slice(0, 80);
  }
  const description = readStringField(value, "description").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  if (description) {
    return description.slice(0, 80);
  }
  return fallback;
}

function createLocalSummary(fullKey: string, currentUserId: number): LocalDraftSummary | null {
  const logicalKey = fullKey.slice(STORAGE_PREFIX.length);
  const parts = logicalKey.split(":");
  if (parts[0] !== "draft") {
    return null;
  }
  const parsed = parseStoredDraft(localStorage.getItem(fullKey));
  if (!parsed) {
    storage.remove(logicalKey);
    return null;
  }

  const primaryKind = parts[1] || "unknown";
  let userId: number | null = null;
  let draftType: DraftType = primaryKind === "comment" || primaryKind === "thread" ? "comment" : "post";
  let entityType = primaryKind === "thread" ? "comment" : primaryKind;
  let kind = primaryKind;
  let issueId: number | null = null;
  let commentableType: string | null = null;
  let commentableId: number | null = null;
  let parentId: number | null = null;

  if (primaryKind === "thread") {
    userId = parseNumber(parts[3]);
    issueId = parseNumber(parts[4]);
    commentableType = "issue";
    commentableId = issueId;
    kind = "thread";
  } else if (primaryKind === "comment") {
    const commentMode = parts[2] || "comment";
    if (commentMode === "new") {
      userId = parseNumber(parts[3]);
      commentableType = parts[4] || null;
      commentableId = parseNumber(parts[5]);
      kind = "comment";
    } else if (commentMode === "principle") {
      userId = parseNumber(parts[4]);
      commentableType = parts[5] || null;
      commentableId = parseNumber(parts[6]);
      kind = "principle";
    } else {
      userId = parseNumber(parts[3]);
      parentId = parseNumber(parts[4]);
      commentableType = "comment";
      commentableId = parentId;
      kind = commentMode;
    }
    entityType = "comment";
  } else {
    userId = parseNumber(parts[3]);
    issueId = parseNumber(parts[4]);
  }

  if (userId !== currentUserId) {
    return null;
  }

  const fallbackTitle = kind === "thread"
    ? "Thread draft"
    : `${kind.charAt(0).toUpperCase()}${kind.slice(1)} draft`;

  return {
    source: "local",
    draftType,
    entityType,
    id: logicalKey,
    storageKey: fullKey,
    logicalKey,
    kind,
    title: summarizeValue(parsed.value, fallbackTitle),
    description: "Saved on this browser",
    value: parsed.value,
    created_at: parsed.savedAtIso,
    updated_at: parsed.savedAtIso,
    issue_id: issueId,
    commentable_type: commentableType,
    commentable_id: commentableId,
    parent_id: parentId,
    root_id: null,
  };
}

export function listLocalDrafts(currentUserId: number | null | undefined): LocalDraftSummary[] {
  if (!currentUserId || typeof localStorage === "undefined") {
    return [];
  }
  const drafts: LocalDraftSummary[] = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (!key || !key.startsWith(DRAFT_PREFIX)) {
      continue;
    }
    const draft = createLocalSummary(key, currentUserId);
    if (draft) {
      drafts.push(draft);
    }
  }
  return drafts.sort((left, right) => {
    const leftTs = new Date(left.updated_at || left.created_at || 0).getTime();
    const rightTs = new Date(right.updated_at || right.created_at || 0).getTime();
    return rightTs - leftTs;
  });
}

export function countLocalDrafts(currentUserId: number | null | undefined): number {
  return listLocalDrafts(currentUserId).length;
}

export function removeLocalDraft(logicalKey: string): void {
  storage.remove(logicalKey);
}
